// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { assignmentAPI, categoryAPI, submissionAPI, courseAPI } from "@/lib/api";
import { CourseGrouping } from "@/components/CourseGrouping";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  FileText, 
  Pencil, 
  Trash2,
  ChevronRight,
  Loader2,
  Calendar as CalendarIcon,
  Clock,
  Eye,
  EyeOff,
  BookOpen,
  Users,
  AlertTriangle,
  Lock,
  Unlock
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, getDaysUntil, cn } from "@/lib/utils";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function AssignmentPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    loadAssignments();
    categoryAPI.list(workspaceId).then(res => setCategories(res.data || [])).catch(() => {});
    courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
  }, [workspaceId]);

  const loadAssignments = async () => {
    try {
      const response = await assignmentAPI.list(workspaceId);
      setAssignments(response.data);
      // Load submission counts for trainers
      if (canManage) {
        const counts = {};
        await Promise.all(response.data.map(async (a) => {
          try {
            const statsRes = await submissionAPI.getStats(workspaceId, a.id);
            counts[a.id] = { submitted: statsRes.data.submitted_count, total: statsRes.data.total_participants };
          } catch (e) { /* ignore */ }
        }));
        setSubmissionCounts(counts);
      }
    } catch (error) {
      console.error("Failed to load assignments:", error);
      toast.error("Erreur lors du chargement des devoirs");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);

    try {
      await assignmentAPI.delete(workspaceId, deleteId);
      setAssignments(assignments.filter(a => a.id !== deleteId));
      toast.success("Devoir supprimé");
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleToggleVisibility = async (assignmentId) => {
    try {
      const response = await assignmentAPI.toggleVisibility(workspaceId, assignmentId);
      setAssignments(assignments.map(a => 
        a.id === assignmentId ? { ...a, is_visible: response.data.is_visible } : a
      ));
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Erreur lors de la modification de la visibilité");
    }
  };

  const handleToggleSubmission = async (assignmentId) => {
    try {
      const response = await assignmentAPI.toggleSubmission(workspaceId, assignmentId);
      setAssignments(assignments.map(a => 
        a.id === assignmentId ? { ...a, submission_open: response.data.submission_open } : a
      ));
      toast.success(response.data.submission_open ? "Soumission activée" : "Soumission désactivée");
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const getStatusBadge = (assignment) => {
    const now = new Date();
    const startDate = new Date(assignment.start_date);
    const dueDate = new Date(assignment.due_date);

    if (now < startDate) {
      return <Badge variant="secondary">À venir</Badge>;
    } else if (now > dueDate) {
      return <Badge variant="outline">Terminé</Badge>;
    } else {
      const daysLeft = getDaysUntil(assignment.due_date);
      if (daysLeft <= 3) {
        return <Badge className="bg-orange-500 text-white">Urgent - {daysLeft}j</Badge>;
      }
      return <Badge className="bg-green-500 text-white">En cours</Badge>;
    }
  };

  // Category-based grouping
  const hasCategories = categories.length > 0;

  const assignmentsByGroup = assignments.reduce((acc, assignment) => {
    let groupKey;
    if (hasCategories) {
      groupKey = assignment.category_id || "__uncategorized__";
    } else {
      groupKey = "__all__";
    }
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(assignment);
    return acc;
  }, {});

  const sortedGroups = (() => {
    if (hasCategories) {
      const result = [];
      categories.forEach(cat => {
        if (assignmentsByGroup[cat.id]) {
          result.push({ key: cat.id, label: cat.name, color: cat.color, items: assignmentsByGroup[cat.id] });
        }
      });
      if (assignmentsByGroup["__uncategorized__"]) {
        result.push({ key: "__uncategorized__", label: "Sans catégorie", color: null, items: assignmentsByGroup["__uncategorized__"] });
      }
      return result;
    } else {
      if (assignmentsByGroup["__all__"]) {
        return [{ key: "__all__", label: "Tous les devoirs", color: null, items: assignmentsByGroup["__all__"] }];
      }
      return [];
    }
  })();

  // Auto-select first tab
  useEffect(() => {
    if (assignments.length === 0) return;
    let currentValid = activeTab && sortedGroups.some(g => g.key === activeTab);
    if (!currentValid) {
      if (sortedGroups.length > 0) {
        setActiveTab(sortedGroups[0].key);
      } else {
        setActiveTab(null);
      }
    }
  }, [assignments, categories, activeTab, sortedGroups]);

  const renderAssignmentCard = (assignment, index, courseColor) => (
    <Card 
      key={assignment.id} 
      className={cn(
        "card-hover relative overflow-hidden pl-2 transition-all"
      )}
      style={courseColor ? { borderColor: `${courseColor}33` } : {}}
      data-testid={`assignment-card-${index}`}
    >
      {courseColor && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-1.5 transition-all" 
          style={{ backgroundColor: courseColor }}
        />
      )}
      <CardContent className="flex items-center gap-4 p-4">
        <div 
          className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 shrink-0 transition-colors"
          style={courseColor ? { backgroundColor: `${courseColor}15` } : {}}
        >
          <FileText 
            className={cn("h-6 w-6 text-primary", courseColor && "text-foreground")} 
            style={courseColor ? { color: courseColor } : {}}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link 
              to={`/workspace/${workspaceId}/assignments/${assignment.id}`}
              className="font-semibold hover:text-primary transition-colors"
            >
              {assignment.title}
            </Link>
            {/* Course attachment badge */}
            {assignment.course_id && (
              <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 bg-blue-50">
                <BookOpen className="h-3 w-3 mr-1" />
                Rattaché
              </Badge>
            )}
            {canManage && assignment.submission_open === false && (
              <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 dark:bg-red-950/30 text-xs">
                <Lock className="h-3 w-3 mr-1" /> Soumission fermée
              </Badge>
            )}
            {canManage && assignment.is_visible === false && (
              <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                Caché
              </Badge>
            )}
            {getStatusBadge(assignment)}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
            {assignment.description || "Pas de description"}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />
              Début: {formatDate(assignment.start_date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Fin: {formatDate(assignment.due_date)}
            </span>
            <span>Note max: {assignment.max_score}</span>
          </div>
        </div>
        {/* Submission count badge */}
        {canManage && submissionCounts[assignment.id] && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-muted/30 shrink-0">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">
              {submissionCounts[assignment.id].submitted}
            </span>
            <span className="text-xs text-muted-foreground">/</span>
            <span className="text-sm text-muted-foreground">
              {submissionCounts[assignment.id].total}
            </span>
            <span className="text-xs text-muted-foreground ml-0.5">remis</span>
          </div>
        )}
        {/* Non remis badge for candidates */}
        {!canManage && (assignment.has_submitted === false) && (new Date() < new Date(assignment.due_date)) && (
          <div className="assignment-not-submitted-badge shrink-0">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>Non remis</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              {/* Visibility Toggle */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleVisibility(assignment.id)}
                      data-testid={`toggle-visibility-assignment-${index}`}
                    >
                      {assignment.is_visible === false ? (
                        <EyeOff className="h-4 w-4 text-yellow-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {assignment.is_visible === false 
                      ? "Rendre visible aux candidats" 
                      : "Cacher aux candidats"
                    }
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Submission Toggle */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleSubmission(assignment.id)}
                    >
                      {assignment.submission_open === false ? (
                        <Lock className="h-4 w-4 text-red-500" />
                      ) : (
                        <Unlock className="h-4 w-4 text-green-500" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {assignment.submission_open === false 
                      ? "Activer la soumission" 
                      : "Désactiver la soumission"
                    }
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Edit Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/workspace/${workspaceId}/assignments/${assignment.id}/edit`)}
                data-testid={`edit-assignment-${index}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleteId(assignment.id)}
                data-testid={`delete-assignment-${index}`}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          )}
          <Link to={`/workspace/${workspaceId}/assignments/${assignment.id}`}>
            <Button variant="ghost" size="icon">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Devoirs</h1>
            <p className="mt-1 text-muted-foreground">
              {assignments.length} devoir(s) dans cet espace
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <CategoryManagerButton />
              <Link to={`/workspace/${workspaceId}/assignments/new`}>
                <Button className="gap-2" data-testid="create-assignment-btn">
                  <Plus className="h-4 w-4" />
                  Nouveau devoir
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Assignments List */}
        {assignments.length === 0 ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucun devoir</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                {canManage 
                  ? "Créez votre premier devoir pour évaluer vos apprenants"
                  : "Aucun devoir disponible pour le moment"
                }
              </p>
              {canManage && (
                <Link to={`/workspace/${workspaceId}/assignments/new`}>
                  <Button className="mt-4 gap-2">
                    <Plus className="h-4 w-4" />
                    Créer un devoir
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="mt-8">
            {/* Category Tabs */}
            {hasCategories && sortedGroups.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-3 mb-6 relative z-10 w-full">
                  {sortedGroups.map((group) => (
                    <div 
                      key={group.key}
                      onClick={() => setActiveTab(group.key)}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all select-none min-w-max",
                        activeTab === group.key 
                          ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary/20" 
                          : "bg-card hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {group.color ? (
                        <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: group.color }} />
                      ) : (
                        <FileText className={cn("h-4 w-4", activeTab === group.key ? "text-primary" : "")} />
                      )}
                      <span className={cn("font-medium text-sm", activeTab === group.key ? "text-foreground" : "")}>
                        {group.label}
                      </span>
                      <span className="text-xs opacity-70 ml-1">{group.items.length}</span>
                    </div>
                  ))}
                </div>

                {/* Active Tab Content */}
                {sortedGroups.map((group) => {
                  if (group.key !== activeTab) return null;
                  return (
                    <div key={group.key} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <CourseGrouping
                        items={group.items}
                        courses={courses}
                        renderItem={renderAssignmentCard}
                        emptyLabel="Sans cours"
                        icon={FileText}
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              /* No categories - group by course directly */
              <CourseGrouping
                items={assignments}
                courses={courses}
                renderItem={renderAssignmentCard}
                emptyLabel="Sans cours"
                icon={FileText}
              />
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce devoir ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le devoir et toutes les soumissions seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </WorkspaceLayout>
  );
}
